import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, Search, Quote } from "lucide-react";

import { api, formatINR, formatCompactNumber } from "@/lib/api";
import { IMG, FEATURED_CAMPAIGNS, TRUSTED_BY, imageForCity, imageForFormat } from "@/lib/images";
import EditorialCarousel from "@/components/common/EditorialCarousel";
import SectionHeader from "@/components/common/SectionHeader";
import AnimatedCounter from "@/components/common/AnimatedCounter";
import SmartImage from "@/components/common/SmartImage";

const FORMATS = [
  { name: "Atrium Domination", category: "Experiential", img: IMG.hangingBanner, note: "Multi-storey hanging displays inside premium-mall atriums." },
  { name: "Backlit Pillars", category: "Out-of-Home", img: IMG.backlitPillar, note: "Wrap structural pillars at every concourse with cinematic creative." },
  { name: "Escalator + Digital", category: "Digital", img: IMG.escalator, note: "Synchronised LED + escalator branding for inescapable narrative." },
  { name: "Food-Court Wraps", category: "Ambient", img: IMG.foodCourt, note: "Tabletops, ceiling clusters and digital menu integrations." },
  { name: "High-Street Hoardings", category: "Out-of-Home", img: IMG.highStreet, note: "Premium urban hoardings in golden-hour catchments." },
  { name: "Kiosks &amp; Activations", category: "Experiential", img: IMG.kiosk, note: "Sampling booths, vehicle reveals, immersive activations." },
];

export default function Home() {
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [cities, setCities] = useState([]);
  const [premium, setPremium] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);
  const heroScale = useTransform(scrollY, [0, 600], [1, 1.1]);

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/case-studies").then((r) => setCases(r.data)).catch(() => {});
    api.get("/public/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/public/inventory?limit=12&is_premium=true").then((r) => setPremium(r.data.items)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-[var(--bone)]">
      {/* ── HERO ───────────────────────────────────────────────────── */}
      <section className="relative h-screen min-h-[680px] overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 will-change-transform">
          <img src={IMG.hero} alt="Premium mall ad space" className="w-full h-full object-cover img-luxe" />
          <div className="absolute inset-0 hero-overlay-strong" />
        </motion.div>

        <div className="relative h-full max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 flex flex-col justify-between py-28 lg:py-32">
          <div className="flex items-start justify-between">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-[11px] tracking-[0.3em] uppercase font-semibold text-[var(--gold-light)] flex items-center gap-3"
              data-testid="hero-pill"
            >
              <span className="inline-block w-8 h-px bg-[var(--gold-light)]" />
              Mall Advertising · Retail Media · India
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="hidden md:block text-right text-[10px] tracking-[0.3em] uppercase text-white/60"
            >
              <div>Édition</div>
              <div className="text-[var(--bone)] mt-1">No. 01 — 2026</div>
            </motion.div>
          </div>

          <div className="max-w-5xl">
            <motion.h1
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="font-serif-display text-white text-6xl md:text-7xl lg:text-[124px] xl:text-[140px] leading-[0.92]"
              data-testid="hero-headline"
            >
              The marketplace<br />
              for India&apos;s <span className="font-serif-italic text-[var(--gold-light)]">malls.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-7 text-white/85 text-lg lg:text-xl leading-relaxed max-w-2xl"
              data-testid="hero-sub"
            >
              {stats?.inventory?.toLocaleString("en-IN") || "3,700+"} live ad spaces. {stats?.malls || 120}+ premium malls.
              {" "}{stats?.cities || 45}+ Indian cities. Discover, plan and book with editorial precision —
              powered by AI.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link to="/marketplace" className="btn-primary !bg-[var(--bone)] !text-[var(--obsidian)] !border-[var(--bone)] hover:!bg-[var(--gold)] hover:!border-[var(--gold)] hover:!text-white" data-testid="hero-cta-primary">
                <Search size={14} /> Browse marketplace
              </Link>
              <Link to="/ai-planner" className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-[var(--obsidian)]" data-testid="hero-cta-secondary">
                <Sparkles size={14} /> Plan with AI
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── BIG STAT STRIP ─────────────────────────────────────────── */}
      <section className="bg-[var(--obsidian)] text-[var(--bone)] border-y border-[var(--border-dark)]" data-testid="stat-strip">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
          {[
            { label: "Ad spaces — live", value: stats?.inventory || 0, fmt: (n) => n.toLocaleString("en-IN") },
            { label: "Premium malls", value: stats?.malls || 0 },
            { label: "Indian cities", value: stats?.cities || 0 },
            { label: "Monthly shoppers", value: (stats?.monthly_footfall || 0) / 1_000_000, suffix: "M+", decimals: 0 },
          ].map((s, idx) => (
            <div key={s.label} className="py-10 lg:py-14 px-4 lg:px-10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-medium">{String(idx + 1).padStart(2, "0")}</div>
              <div className="font-serif-display mt-4 text-5xl lg:text-6xl">
                {s.fmt ? s.fmt(s.value) : <AnimatedCounter value={s.value} suffix={s.suffix || ""} decimals={s.decimals || 0} />}
              </div>
              <div className="mt-3 text-[11px] uppercase tracking-[0.25em] text-[var(--gold-light)]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── TRUSTED BY MARQUEE ─────────────────────────────────────── */}
      <section className="border-b border-[var(--border)] py-8 overflow-hidden bg-[var(--bone)]" data-testid="trusted-marquee">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[var(--slate-2)] mb-5 text-center">
            Trusted by category leaders
          </div>
        </div>
        <div className="flex marquee-track gap-20 whitespace-nowrap">
          {[...TRUSTED_BY, ...TRUSTED_BY, ...TRUSTED_BY].map((b, idx) => (
            <span key={idx} className="font-serif text-2xl tracking-[0.18em] text-[var(--slate-2)] hover:text-[var(--ink)] transition-colors">
              {b}
            </span>
          ))}
        </div>
      </section>

      {/* ── FEATURED CAMPAIGNS CAROUSEL — JCDecaux style ───────────── */}
      <section className="bg-[var(--bone)] py-24 lg:py-32" data-testid="campaigns-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <SectionHeader
              eyebrow="Creative · OOH"
              title="Campaigns that turn footfall into"
              italic="cultural moments."
              sub="Editorial showcases of recent campaigns shipped across Indian malls and high streets."
            />
            <Link to="/case-studies" className="btn-ghost self-start lg:self-end">
              See all work <ArrowUpRight size={14} />
            </Link>
          </div>

          <EditorialCarousel
            testId="campaign-carousel"
            slideClassName="w-full md:w-2/3 lg:w-1/2 pr-6"
            options={{ loop: true, align: "start" }}
          >
            {FEATURED_CAMPAIGNS.map((c, idx) => (
              <Link
                key={idx}
                to="/case-studies"
                className="block group"
                data-testid={`campaign-slide-${idx}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={c.image} alt={c.brand} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 img-luxe" />
                  <div className="absolute inset-0 hero-overlay" />
                  <div className="absolute top-5 left-5 chip bg-white/90 backdrop-blur">{c.format}</div>
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold-light)] mb-2">
                      {String(idx + 1).padStart(2, "0")} · Campaign
                    </div>
                    <div className="font-serif-display text-3xl lg:text-4xl leading-tight">
                      {c.brand} <span className="opacity-70">|</span> <span className="font-serif-italic text-[var(--gold-light)]">{c.city}</span>
                    </div>
                    <div className="mt-2 text-sm text-white/85 max-w-md">{c.caption}</div>
                  </div>
                </div>
              </Link>
            ))}
          </EditorialCarousel>
        </div>
      </section>

      {/* ── PULL QUOTE / EDITORIAL STATEMENT ───────────────────────── */}
      <section className="bg-[var(--obsidian)] text-[var(--bone)] py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={IMG.luxuryGradient} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-2">
            <Quote size={64} strokeWidth={1} className="text-[var(--gold-light)]" />
          </div>
          <blockquote className="lg:col-span-10 font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.1]">
            India&apos;s mall floor is the most under-used <span className="font-serif-italic text-[var(--gold-light)]">media surface</span> on earth.
            Fundle is how the next decade of brands will book it — at <span className="font-serif-italic text-[var(--gold-light)]">editorial speed.</span>
          </blockquote>
        </div>
      </section>

      {/* ── FORMATS · MAGAZINE GRID ────────────────────────────────── */}
      <section className="bg-[var(--bone)] py-24 lg:py-32" data-testid="formats-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <SectionHeader
            eyebrow="Formats"
            title="Every surface inside India&apos;s malls,"
            italic="indexed and bookable."
            sub="From atriums to escalators, food courts to high-streets — beautifully shot and ready to ship."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
            {FORMATS.map((f, idx) => (
              <motion.div
                key={f.name}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: idx * 0.06 }}
                className="bg-[var(--bone)] group"
                data-testid={`format-card-${idx}`}
              >
                <Link to="/marketplace" className="block">
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img src={f.img} alt={f.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 img-luxe" />
                    <div className="absolute inset-0 hero-overlay" />
                    <div className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.25em] text-white/90">{f.category}</div>
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <div className="font-serif-display text-3xl">{f.name}</div>
                      <div className="mt-2 text-sm text-white/80 max-w-xs">{f.note}</div>
                      <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[var(--gold-light)]">
                        View placements <ArrowRight size={12} />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI PLANNER · MAGAZINE SPREAD ───────────────────────────── */}
      <section className="bg-[var(--obsidian)] text-[var(--bone)] py-24 lg:py-32" data-testid="ai-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--gold-light)] font-semibold flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[var(--gold-light)]" /> AI Media Planner
            </div>
            <h2 className="mt-6 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              Plan a 10-city campaign in <span className="font-serif-italic text-[var(--gold-light)]">90 seconds.</span>
            </h2>
            <p className="mt-6 text-white/75 text-lg leading-relaxed">
              Tell our planner what you sell, who you&apos;re after and what you&apos;ll spend.
              Receive a structured, magazine-style media plan — malls, formats, reach, KPIs,
              creative directions — with the reasoning behind every recommendation.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {["City weights", "Format mix", "Mall list", "Budget split"].map((k) => (
                <div key={k} className="border border-white/15 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">{k}</div>
                  <div className="mt-1 text-[var(--gold-light)] font-medium">Recommended</div>
                </div>
              ))}
            </div>
            <Link to="/ai-planner" className="mt-10 btn-primary !bg-[var(--gold)] !border-[var(--gold)] !text-white hover:!bg-[var(--bone)] hover:!text-[var(--obsidian)] hover:!border-[var(--bone)] inline-flex">
              <Sparkles size={14} /> Open the planner
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="relative aspect-[4/5] lg:aspect-[5/4] overflow-hidden border border-white/15">
              <img src={IMG.aiPlannerMag} alt="AI planner editorial layout" className="absolute inset-0 w-full h-full object-cover img-luxe" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[var(--obsidian)]/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-3">
                <Stat label="Reach" value="14.8M" />
                <Stat label="Impressions" value="92M" />
                <Stat label="Engagement" value="6.4%" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LIVE INVENTORY CAROUSEL ────────────────────────────────── */}
      <section className="bg-[var(--bone)] py-24 lg:py-32" data-testid="inventory-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <SectionHeader
              eyebrow="Live Inventory"
              title="Trending ad spaces"
              italic="right now."
              sub={`Fresh availability across ${stats?.malls || 120}+ premium Indian malls.`}
            />
            <Link to="/marketplace" className="btn-ghost self-start lg:self-end">
              View all {stats?.inventory?.toLocaleString("en-IN") || ""} spaces <ArrowUpRight size={14} />
            </Link>
          </div>
          <EditorialCarousel
            testId="inventory-carousel"
            slideClassName="w-2/3 sm:w-1/2 md:w-1/3 lg:w-1/4 pr-5"
            options={{ loop: false, align: "start" }}
          >
            {premium.slice(0, 12).map((spot, idx) => (
              <Link key={spot.id} to={`/inventory/${spot.id}`} className="block group" data-testid={`inv-slide-${idx}`}>
                <div className="relative aspect-[4/5] overflow-hidden">
                  <SmartImage src={spot.photos?.[0]} format={spot.format} alt={spot.format} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 img-luxe" />
                  <div className="absolute inset-0 hero-overlay" />
                  <div className="absolute top-4 left-4 chip bg-white/95">{spot.format_category}</div>
                  {spot.is_premium && <div className="absolute top-4 right-4 chip chip-gold chip-active">Premium</div>}
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <div className="font-serif text-xl leading-tight">{spot.format}</div>
                    <div className="text-xs text-white/80 mt-1 truncate">{spot.mall_name} · {spot.city}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[var(--ink)] font-medium text-sm">
                    {formatINR(spot.price_per_day)}<span className="text-[var(--slate-2)] font-normal text-xs">/day</span>
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--slate-2)] font-mono">{formatCompactNumber(spot.daily_footfall || spot.monthly_footfall)} ftfl</div>
                </div>
              </Link>
            ))}
          </EditorialCarousel>
        </div>
      </section>

      {/* ── CITIES ─────────────────────────────────────────────────── */}
      <section className="bg-[var(--bone-warm)] py-24 lg:py-32" data-testid="cities-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <SectionHeader
            eyebrow="National Footprint"
            title="From Mumbai to Calicut —"
            italic="India, indexed."
            sub="Tier-1 dominance, Tier-2 expansion. Every premium catchment in one marketplace."
          />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[var(--border)]">
            {cities.slice(0, 8).map((c, idx) => (
              <motion.div
                key={c.city}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className="bg-[var(--bone-warm)] group"
              >
                <Link to={`/cities/${encodeURIComponent(c.city)}`} className="block" data-testid={`city-card-${c.city}`}>
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img src={imageForCity(c.city) || c.hero_image} alt={c.city} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 img-luxe" />
                    <div className="absolute inset-0 hero-overlay" />
                    <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] text-white/85">
                      {String(idx + 1).padStart(2, "0")} / {c.state || "India"}
                    </div>
                    <div className="absolute bottom-5 left-5 right-5 text-white">
                      <div className="font-serif-display text-3xl">{c.city}</div>
                      <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[var(--gold-light)]">
                        {c.mall_count} malls · {formatCompactNumber(c.monthly_footfall)}/mo
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/cities" className="btn-secondary">All cities <ArrowUpRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* ── FOR BRANDS / FOR MALLS — JCDecaux dual ─────────────────── */}
      <section className="bg-[var(--bone)] py-24 lg:py-32" data-testid="audience-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-px bg-[var(--ink)]">
          <AudienceCard
            label="For brands"
            title="Every premium mall in India,"
            italic="one workflow."
            body="Search 3,700+ ad spaces, build proposals with AI, lock pricing, manage creatives and track campaigns — all in a single editorial-grade dashboard."
            cta="Plan a campaign"
            href="/ai-planner"
            image={IMG.kiosk}
          />
          <AudienceCard
            label="For mall owners"
            title="Unlock the next layer of"
            italic="retail revenue."
            body="List your inventory, set availability, approve creatives and access the highest-quality demand pool in Indian retail media."
            cta="Become a partner"
            href="/contact"
            image={IMG.exteriorLightbox}
          />
        </div>
      </section>

      {/* ── CASE STUDIES PREVIEW ───────────────────────────────────── */}
      <section className="bg-[var(--bone-warm)] py-24 lg:py-32" data-testid="cases-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <SectionHeader
              eyebrow="Selected work"
              title="Outcomes, in"
              italic="black and white."
            />
            <Link to="/case-studies" className="btn-ghost self-start lg:self-end">All work <ArrowUpRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {cases.slice(0, 4).map((c, idx) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: idx * 0.07 }}
              >
                <Link to={`/case-studies/${c.id}`} className="block group" data-testid={`case-card-${idx}`}>
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <SmartImage src={c.cover_image} fallback={IMG.aiPlannerMag} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 img-luxe" />
                    <div className="absolute inset-0 hero-overlay" />
                    <div className="absolute top-5 left-5 chip bg-white/95">{c.industry}</div>
                  </div>
                  <div className="mt-5 flex items-start justify-between gap-6">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold)]">{c.brand} <span className="opacity-50">|</span> {c.cities[0]}</div>
                      <div className="mt-2 font-serif-display text-2xl md:text-3xl text-[var(--ink)] leading-tight">{c.title}</div>
                    </div>
                    <ArrowUpRight size={22} className="text-[var(--ink)] shrink-0 mt-2 group-hover:text-[var(--gold)] transition-colors" />
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-3 max-w-md">
                    <Pill v={`${c.reach_millions}M`} l="Reach" />
                    <Pill v={`${c.impressions_millions}M`} l="Impressions" />
                    <Pill v={`${c.engagement_rate}%`} l="Engagement" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ──────────────────────────────────────────────── */}
      <section className="relative py-32 lg:py-40 overflow-hidden" data-testid="home-final-cta">
        <div className="absolute inset-0">
          <img src={IMG.luxuryGradient} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 lg:px-16 text-center">
          <div className="text-[11px] tracking-[0.3em] uppercase font-semibold text-[var(--gold)]">Get started</div>
          <h2 className="mt-5 font-serif-display text-5xl md:text-6xl lg:text-7xl text-[var(--ink)]">
            Put your brand inside <br className="hidden md:block" />
            India&apos;s <span className="font-serif-italic text-[var(--gold)]">malls — today.</span>
          </h2>
          <p className="mt-7 text-[var(--slate-1)] text-lg max-w-2xl mx-auto">
            Browse 3,700+ live ad spaces. Or let the AI Planner draft your first proposal in 90 seconds.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/marketplace" className="btn-primary"><Search size={14} /> Browse marketplace</Link>
            <Link to="/contact" className="btn-secondary">Talk to a strategist <ArrowUpRight size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white/10 backdrop-blur-md p-4 border border-white/20 text-center">
      <div className="font-serif-display text-2xl text-[var(--gold-light)]">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-white/70">{label}</div>
    </div>
  );
}

function Pill({ v, l }) {
  return (
    <div>
      <div className="font-serif text-xl text-[var(--ink)]">{v}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--slate-2)]">{l}</div>
    </div>
  );
}

function AudienceCard({ label, title, italic, body, cta, href, image }) {
  return (
    <Link to={href} className="relative bg-[var(--bone)] p-10 lg:p-16 group overflow-hidden block min-h-[480px] flex flex-col justify-between" data-testid={`audience-${label.replace(/\s/g, "-")}`}>
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
        <img src={image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[var(--obsidian)]/60" />
      </div>
      <div className="relative">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--gold)] group-hover:text-[var(--gold-light)]">{label}</div>
        <div className="mt-6 font-serif-display text-3xl md:text-4xl lg:text-5xl text-[var(--ink)] group-hover:text-white transition-colors">
          {title} <span className="font-serif-italic text-[var(--gold)] group-hover:text-[var(--gold-light)]">{italic}</span>
        </div>
        <p className="mt-5 text-[var(--slate-1)] group-hover:text-white/85 transition-colors text-base lg:text-lg leading-relaxed max-w-md">
          {body}
        </p>
      </div>
      <div className="relative mt-10 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] font-semibold text-[var(--ink)] group-hover:text-[var(--gold-light)] transition-colors">
        <span className="gold-rule" /> {cta} <ArrowRight size={14} />
      </div>
    </Link>
  );
}
